/**
 * Hooks for admin AI generation history.
 *
 * Provides data fetching for AI generation history with filtering,
 * pagination, and aggregate statistics.
 */

import { useState, useEffect, useCallback } from "react";
import { apiGet } from "../lib/api";
import type { DisciplineSlug } from "@jumpbean/shared";

// ----- Types -----

export type GenerationStatus = "pending" | "completed" | "failed" | "partial";

export interface AIGenerationHistoryItem {
  id: string;
  admin_id: string;
  discipline: DisciplineSlug;
  level: number;
  challenge_type: string;
  count: number;
  target_skills: string[] | null;
  model_used: string;
  status: GenerationStatus;
  challenges_generated: number;
  challenges_approved: number;
  tokens_input: number | null;
  tokens_output: number | null;
  duration_ms: number | null;
  agent_run_id: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface AIHistoryFilters {
  discipline?: DisciplineSlug;
  status?: GenerationStatus;
  dateFrom?: string;
  dateTo?: string;
}

export interface AIHistoryStats {
  totalGenerations: number;
  totalChallengesGenerated: number;
  totalChallengesApproved: number;
  approvalRate: number;
  byDiscipline: Record<DisciplineSlug, number>;
  byStatus: Record<GenerationStatus, number>;
}

interface HistoryApiResponse {
  success: boolean;
  data: {
    history: AIGenerationHistoryItem[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  };
}

// ----- useAdminAIHistory Hook -----

export function useAdminAIHistory() {
  const [history, setHistory] = useState<AIGenerationHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AIHistoryFilters>({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false,
  });

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Build query params
      const params = new URLSearchParams();
      params.set("limit", String(pagination.limit));
      params.set("offset", String((pagination.page - 1) * pagination.limit));

      if (filters.discipline) {
        params.set("discipline", filters.discipline);
      }

      if (filters.status) {
        params.set("status", filters.status);
      }

      const result = await apiGet<HistoryApiResponse>(
        `/api/admin/ai/history?${params.toString()}`
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      const data = result.data?.data;
      if (!data) {
        throw new Error("No history data received");
      }

      setHistory(data.history);
      setPagination((prev) => ({
        ...prev,
        total: data.pagination.total,
        hasMore: data.pagination.hasMore,
      }));
    } catch (err) {
      console.error("Failed to fetch AI history:", err);
      setError(err instanceof Error ? err.message : "Failed to load AI history");
      setHistory([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters.discipline, filters.status, pagination.page, pagination.limit]);

  // Fetch on mount and when filters/pagination change
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Update filters
  const updateFilters = useCallback((newFilters: Partial<AIHistoryFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters({});
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Pagination controls
  const goToPage = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  const nextPage = useCallback(() => {
    setPagination((prev) => {
      if (prev.hasMore) {
        return { ...prev, page: prev.page + 1 };
      }
      return prev;
    });
  }, []);

  const prevPage = useCallback(() => {
    setPagination((prev) => {
      if (prev.page > 1) {
        return { ...prev, page: prev.page - 1 };
      }
      return prev;
    });
  }, []);

  // Calculate stats from history
  const getStats = useCallback((): AIHistoryStats => {
    const stats: AIHistoryStats = {
      totalGenerations: pagination.total,
      totalChallengesGenerated: 0,
      totalChallengesApproved: 0,
      approvalRate: 0,
      byDiscipline: {
        math: 0,
        spelling: 0,
        patterns: 0,
        typing: 0,
        programming: 0,
      },
      byStatus: {
        pending: 0,
        completed: 0,
        failed: 0,
        partial: 0,
      },
    };

    // Calculate stats from the current page of history
    // Note: For accurate totals, we would need a dedicated stats endpoint
    for (const item of history) {
      stats.totalChallengesGenerated += item.challenges_generated;
      stats.totalChallengesApproved += item.challenges_approved;
      stats.byDiscipline[item.discipline]++;
      stats.byStatus[item.status]++;
    }

    stats.approvalRate =
      stats.totalChallengesGenerated > 0
        ? Math.round(
            (stats.totalChallengesApproved / stats.totalChallengesGenerated) * 100
          )
        : 0;

    return stats;
  }, [history, pagination.total]);

  // Get total pages
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return {
    history,
    isLoading,
    error,
    filters,
    pagination: {
      ...pagination,
      totalPages,
    },
    updateFilters,
    clearFilters,
    goToPage,
    nextPage,
    prevPage,
    getStats,
    refetch: fetchHistory,
  };
}

// ----- Helper Functions -----

/**
 * Format duration in milliseconds to human-readable string
 */
export function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return "-";

  if (ms < 1000) {
    return `${ms}ms`;
  }

  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Format token count with thousands separator
 */
export function formatTokens(tokens: number | null): string {
  if (tokens === null || tokens === undefined) return "-";
  return tokens.toLocaleString();
}

/**
 * Get status color classes
 */
export function getStatusColor(status: GenerationStatus): {
  bg: string;
  text: string;
  border: string;
} {
  switch (status) {
    case "completed":
      return { bg: "bg-green-100", text: "text-green-700", border: "border-green-200" };
    case "failed":
      return { bg: "bg-red-100", text: "text-red-700", border: "border-red-200" };
    case "partial":
      return { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200" };
    case "pending":
    default:
      return { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200" };
  }
}

/**
 * Format date to readable string
 */
export function formatHistoryDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
